import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState, SearchInput, matchText } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Footprints, UserCheck, Eye } from "lucide-react";
import LeadFormDialog, { EMPTY_LEAD, leadPayload } from "@/components/leads/LeadFormDialog";
import WalkthroughDialog from "@/components/leads/WalkthroughDialog";
import { LEAD_STATUSES, statusMeta } from "@/components/leads/leadMeta";

function StatusFilter({ items, value, onChange }) {
  const opts = [{ v: "", l: "All" }, ...LEAD_STATUSES];
  return (
    <div className="flex flex-wrap gap-2" data-testid="lead-status-filter">
      {opts.map((s) => {
        const n = s.v ? items.filter((l) => l.status === s.v).length : items.length;
        return (
          <button key={s.v || "all"} type="button" onClick={() => onChange(s.v)} data-testid={`lead-filter-${s.v || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value === s.v ? "bg-brand-gradient text-white border-transparent" : "border-border/70 text-muted-foreground hover:bg-accent/60"}`}>
            {s.l} <span className="opacity-70">{n}</span>
          </button>
        );
      })}
    </div>
  );
}

function VisitCell({ lead, team, onBook, onOpen }) {
  const w = lead.walkthrough;
  if (!w) {
    if (["won", "lost"].includes(lead.status)) return <span className="text-muted-foreground">—</span>;
    return (
      <Button size="sm" variant="outline" onClick={onBook} data-testid={`lead-book-wt-${lead.id}`} className="rounded-sm gap-1.5 h-8">
        <Footprints className="w-3.5 h-3.5" /> Book walk-through
      </Button>
    );
  }
  const names = w.assigned_team_ids.map((id) => team.find((t) => t.id === id)?.name).filter(Boolean).join(", ");
  return (
    <button type="button" onClick={onOpen} className="text-left hover:underline" data-testid={`lead-wt-${lead.id}`}>
      <div className="text-sm font-medium text-[#0f766e]">{format(parseISO(w.scheduled_date), "EEE d MMM · h:mma")}</div>
      <div className="text-xs text-muted-foreground">{w.status === "completed" ? "Visited" : names || "Unassigned"}</div>
    </button>
  );
}

export default function Leads() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [editLead, setEditLead] = useState(null);
  const [wtLead, setWtLead] = useState(null);
  const [wtJob, setWtJob] = useState(null);

  const load = () => api.get("/leads").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!params.get("new")) return;
    setEditLead({ ...EMPTY_LEAD });
    params.delete("new"); setParams(params, { replace: true });
  }, [params, setParams]);

  const shown = items.filter((l) => (!filter || l.status === filter) && matchText(search, l.name, l.phone, l.email, l.suburb, l.source));

  const setStatus = async (l, status) => {
    try {
      await api.put(`/leads/${l.id}`, { ...leadPayload(l), status });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete lead ${l.name}? Any upcoming walk-through will be removed.`)) return;
    await api.delete(`/leads/${l.id}`);
    toast.success("Lead deleted"); load();
  };
  const convert = async (l) => {
    if (!window.confirm(`Convert ${l.name} to a client and open a new booking for them?`)) return;
    try {
      const { data } = await api.post(`/leads/${l.id}/convert`);
      toast.success(`${data.name} is now a client — finish their first booking`);
      navigate(`/jobs?client=${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openVisit = async (l) => {
    const { data } = await api.get("/jobs");
    setWtJob(data.find((j) => j.id === l.walkthrough.id) || null);
  };

  return (
    <div data-testid="leads-page">
      <PageHeader eyebrow="Enquiries" title="Leads"
        description="Track enquiries, book a free walk-through, then convert them to a client in one click."
        action={<Button onClick={() => setEditLead({ ...EMPTY_LEAD })} data-testid="add-lead-button" className="rounded-sm gap-2"><Plus className="w-4 h-4" /> New Lead</Button>} />

      {items.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <StatusFilter items={items} value={filter} onChange={setFilter} />
          <SearchInput value={search} onChange={setSearch} placeholder="Search leads…" testid="leads-search" />
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="No leads yet" description="Add an enquiry to book them a free walk-through visit."
          action={<Button onClick={() => setEditLead({ ...EMPTY_LEAD })} className="rounded-sm">Add Lead</Button>} />
      ) : shown.length === 0 ? (
        <EmptyState title="No matches" description="No leads match this filter." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="label-caps">Lead</TableHead>
              <TableHead className="label-caps">Source</TableHead>
              <TableHead className="label-caps">Status</TableHead>
              <TableHead className="label-caps">Walk-through</TableHead>
              <TableHead className="text-right label-caps">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell className="py-4">
                  <div className="font-medium">{l.name}</div>
                  <div className="text-sm text-muted-foreground">{[l.phone, l.email].filter(Boolean).join(" · ") || "No contact details"}</div>
                  {l.suburb && <div className="text-xs text-muted-foreground">{l.suburb}</div>}
                </TableCell>
                <TableCell className="text-sm">{l.source || "—"}</TableCell>
                <TableCell>
                  <select value={l.status} onChange={(e) => setStatus(l, e.target.value)} data-testid={`lead-status-${l.id}`}
                    className={`rounded-full px-3 py-1 text-xs font-medium border-0 outline-none cursor-pointer ${statusMeta(l.status).cls}`}>
                    {LEAD_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </TableCell>
                <TableCell><VisitCell lead={l} team={team} onBook={() => setWtLead(l)} onOpen={() => openVisit(l)} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {l.customer_id ? (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/clients/${l.customer_id}`)} data-testid={`lead-view-client-${l.id}`} className="rounded-sm gap-1.5 h-8">
                        <Eye className="w-3.5 h-3.5" /> View client
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => convert(l)} data-testid={`lead-convert-${l.id}`} className="rounded-sm gap-1.5 h-8">
                        <UserCheck className="w-3.5 h-3.5" /> Convert to client
                      </Button>
                    )}
                    {l.walkthrough && !["won", "lost"].includes(l.status) && (
                      <button onClick={() => setWtLead(l)} title="Book another walk-through" data-testid={`lead-rebook-wt-${l.id}`} className="p-2 hover:text-primary text-muted-foreground"><Footprints className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => setEditLead(l)} data-testid={`lead-edit-${l.id}`} className="p-2 hover:text-primary text-muted-foreground"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(l)} data-testid={`lead-delete-${l.id}`} className="p-2 hover:text-destructive text-muted-foreground"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <LeadFormDialog lead={editLead} onClose={() => setEditLead(null)}
        onSaved={(data, isNew) => { load(); if (isNew) setWtLead(data); }} />
      <WalkthroughDialog lead={wtLead} job={wtJob} team={team} onClose={() => { setWtLead(null); setWtJob(null); }} onSaved={load} />
    </div>
  );
}
